import type { MapTool, ToolId, ToolState } from "./types";

type Listener = (state: ToolState) => void;

export class ToolStateMachine {
	private _tools = new Map<ToolId, MapTool>();
	private _state: ToolState = { activeTool: null, previousTool: null };
	private _listeners = new Set<Listener>();

	get state(): ToolState {
		return { ...this._state };
	}

	get cursor(): string | undefined {
		if (!this._state.activeTool) return undefined;
		return this._tools.get(this._state.activeTool)?.cursor;
	}

	register(tool: MapTool): void {
		const wasActive = this._state.activeTool === tool.id;
		if (wasActive) {
			// Deactivate old instance before replacing
			this._tools.get(tool.id)?.onDeactivate?.();
		}
		this._tools.set(tool.id, tool);
		if (wasActive) {
			tool.onActivate?.();
		}
	}

	getRegistered(id: ToolId): MapTool | null {
		return this._tools.get(id) ?? null;
	}

	activate(id: ToolId): void {
		if (this._state.activeTool === id) return;
		const tool = this._tools.get(id);
		if (!tool) throw new Error(`Tool "${id}" not registered`);

		this._deactivateCurrent();
		this._state.activeTool = id;
		tool.onActivate?.();
		this._notify();
	}

	deactivate(): void {
		if (!this._state.activeTool) return;
		this._deactivateCurrent();
		this._state.activeTool = null;
		this._notify();
	}

	toggle(id: ToolId): void {
		if (this._state.activeTool === id) {
			const tool = this._tools.get(id);
			if (tool?.onToggleMode?.()) {
				this._notify(); // re-render to update label/cursor
			} else {
				this.deactivate();
			}
		} else {
			this.activate(id);
		}
	}

	onChange(listener: Listener): () => void {
		this._listeners.add(listener);
		return () => this._listeners.delete(listener);
	}

	private _deactivateCurrent(): void {
		if (!this._state.activeTool) return;
		const current = this._tools.get(this._state.activeTool);
		current?.onDeactivate?.();
		this._state.previousTool = this._state.activeTool;
	}

	private _notify(): void {
		const snapshot = this.state;
		for (const l of this._listeners) l(snapshot);
	}
}
